---
title: 'Step 4: Configure the Collector'
install_track_title: Installation Guide (Amazon RDS and Amazon Aurora)
backlink_href: /docs/install
backlink_title: 'Installation Guide'
---

import PublicLastStepLogInsightsLink from '../_public_last_step_log_insights_link.mdx'

export const APIKeyInstructions = ({ apiKey }) => {
  return apiKey ? (
    <React.Fragment>
      The <code>api_key</code> for the current organization is{" "}
      <code>{apiKey}</code>
    </React.Fragment>
  ) : (
    <React.Fragment>
      The <code>api_key</code> can be found on the pganalyze Settings page for
      your organization under the API Access tab
    </React.Fragment>
  );
};

export const ConfigSample = ({ apiKey }) => {
  return (
    <CodeBlock>
      {`[pganalyze]
api_key = ${apiKey ?? 'your_pga_organization_api_key'}\n
[instance1]
db_host = instance-id.account-id.us-east-1.rds.amazonaws.com
db_name = your_database_name
db_username = your_monitoring_user
db_password = your_monitoring_user_password
db_sslrootcert = rds-ca-global
db_sslmode = verify-full`}
    </CodeBlock>
  )
}

## Configuring the collector on an Amazon EC2 instance

The collector configuration file lives in `/etc/pganalyze-collector.conf`, and looks like this:

<ConfigSample apiKey={props.apiKey} />

**Fill in the values step-by-step:**

1. <APIKeyInstructions apiKey={props.apiKey} />
2. The `db_host` is the hostname / endpoint of your RDS instance (for Amazon Aurora you can use the cluster endpoint in many cases, see for details below)
3. The `db_name` is the database name on the Postgres instance you want to monitor
4. The `db_username` and `db_password` should be the monitoring user we created in <Link to='02_create_monitoring_user'>Step 2</Link>
5. The `db_sslrootcert` and `db_sslmode` is the recommended SSL connection configuration that you can usually keep as specified above

**Note:** The pganalyze collector allows for more [optional settings](https://pganalyze.com/docs/collector/settings) (e.g. AWS access keys, multiple database names)

## Handling Amazon Aurora clusters vs instances

In the case of Amazon Aurora, the collector automatically resolves `cluster` endpoints to the underlying writer instance, and `cluster-ro` to the underlying reader instance:

<CodeBlock language="text">
{`[writer_instance]
db_host = mydbcluster.cluster-123456789012.us-east-1.rds.amazonaws.com
...
 
[reader_instance]
db_host = mydbcluster.cluster-ro-123456789012.us-east-1.rds.amazonaws.com
...`}
</CodeBlock>

For writer instances this is supported for any size of cluster, but for
reader instances this is only supported in two-node clusters (i.e. single reader instance).

If you have a cluster with multiple readers, you instead need to specify each instance endpoint separately:

<CodeBlock language="text">
{`[instance1]
db_host = mydbinstance1.123456789012.us-east-1.rds.amazonaws.com
...
 
[instance2]
db_host = mydbinstance2.123456789012.us-east-1.rds.amazonaws.com
...
 
[instance3]
db_host = mydbinstance3.123456789012.us-east-1.rds.amazonaws.com
...`}
</CodeBlock>

## Test snapshot

Now, verify that the configuration is correct, by running the following command:

<CodeBlock language="bash">
{`$ sudo pganalyze-collector --test
1999/01/01 08:04:30 I [pganalyze] Testing statistics collection...
1999/01/01 08:04:32 I [pganalyze] Test submission successful (1010 KB received)`}
</CodeBlock>

<PublicLastStepLogInsightsLink />
